/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  FormHelperText,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import logStore from "../../stores/LogStore";
import deviceStore from "../../stores/DeviceStore";
import { filterSingleDeviceData } from "../../utils/logDataFilter&Cleaner.js";

const COLOR_PALETTE = [
  "#1976d2",
  "#dc004e",
  "#388e3c",
  "#ff9800",
  "#9c27b0",
  "#03a9f4",
  "#f44336",
  "#009688",
];

// const deviceIdToNameMap = {
//   "373137381932343431332F55": "AGAMA 2",
//   "373137381932343431332F57": "PS-157",
//   "3637544A163832323032106A": "PR-004",
//   Bat456: "Test Device 1",
//   Rat789: "Test Device 2",
//   1234: "Prototype Device 1",
//   12345: "New Device",
// };

const CustomLegend = ({ series, onToggle }) => {
  const safeSeries = Array.isArray(series) ? series : [];

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        mb: 2,
        gap: 1,
      }}
    >
      {safeSeries.map((s) => (
        <Chip
          key={s.label}
          label={
            <Typography variant="caption" sx={{ fontWeight: "medium" }}>
              {s.label}
            </Typography>
          }
          size="small"
          sx={{
            backgroundColor: s.visible
              ? "rgba(0,0,0,0.05)"
              : "rgba(0,0,0,0.03)",
            opacity: s.visible ? 1 : 0.5,
            cursor: "pointer",
          }}
          avatar={
            <Box
              sx={{
                width: 16,
                height: 16,
                backgroundColor: s.color || "#aaa",
                borderRadius: "50%",
                ml: 1,
              }}
            />
          }
          onClick={() => onToggle && onToggle(s.label)}
        />
      ))}
    </Box>
  );
};

const ChartContainer = ({ title, legendSeries, onToggle, children, sx }) => (
  <Box
    sx={{
      borderRadius: 2,
      height: 500,
      p: 3,
      display: "flex",
      flexDirection: "column",
      mb: 4,
      ...sx,
    }}
  >
    <Typography
      variant="h6"
      sx={{ textAlign: "center", mb: 2, fontWeight: "bold", color: "#1976d2" }}
    >
      {title}
    </Typography>
    <CustomLegend
      series={Array.isArray(legendSeries) ? legendSeries : []}
      onToggle={onToggle}
    />
    <Box sx={{ flexGrow: 1 }}>{children}</Box>
  </Box>
);

const AnalyticTab = observer(() => {
  const [visibleSeries, setVisibleSeries] = useState({});
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [connectionAttempts, setConnectionAttempts] = useState(0);
  const MAX_SELECTION = 3;

  useEffect(() => {
    if (!logStore.isConnected) logStore.connect();
    logStore.setDevice(null);
  }, []);

  const allLogs = (logStore.currentLogs ?? []).map((log) => {
    const deviceId = String(
      log.device_id || log.dev_id || log.data?.device_id
    ).trim();
    const deviceName = deviceStore.getDeviceName(deviceId) || deviceId;
    const { valve1Data, valve2Data } = filterSingleDeviceData(log);
    return {
      device_id: deviceId,
      device_name: deviceName,
      valve1Data,
      valve2Data,
    };
  });

  const latestLogsByDevice = allLogs.reduce((acc, log) => {
    const device = log.device_id;
    const logTime = new Date(log.valve1Data?.timestamp ?? 0);
    const currentTime = new Date(acc[device]?.valve1Data?.timestamp ?? 0);
    if (!acc[device] || logTime > currentTime) acc[device] = log;
    return acc;
  }, {});

  const devicesWithoutData = selectedDevices.filter(
    (id) => !latestLogsByDevice[id]
  );

  // const allDeviceIds = Object.keys(latestLogsByDevice).sort();
  const allDeviceIds = Object.keys(deviceStore.deviceNameMap);

  useEffect(() => {
    if (allDeviceIds.length > 0 && selectedDevices.length === 0) {
      setSelectedDevices(allDeviceIds.slice(0, 3));
    }
  }, [allDeviceIds]);

  const isNumeric = (v) => typeof v === "number" && !isNaN(v);

  const EXCLUDED_KEYS = ["v1_stat_1", "v1_stat_2", "v2_stat_1", "v2_stat_2"];

  const extractSeries = (section) => {
    const firstValidDevice = selectedDevices.find(
      (id) =>
        latestLogsByDevice[id] &&
        typeof latestLogsByDevice[id][section] === "object"
    );

    if (!firstValidDevice) return [];

    const keys = Object.keys(
      latestLogsByDevice[firstValidDevice][section]
    ).filter(
      (key) =>
        key !== "timestamp" &&
        isNumeric(latestLogsByDevice[firstValidDevice][section][key]) &&
        !EXCLUDED_KEYS.includes(key)
    );

    return keys.map((key, index) => ({
      label: key,
      id: key,
      data: selectedDevices.map(
        (id) => latestLogsByDevice[id]?.[section]?.[key] ?? null
      ),
      color: COLOR_PALETTE[index % COLOR_PALETTE.length],
      visible: visibleSeries[key] !== false,
    }));
  };

  const toggleSeriesVisibility = (key) => {
    setVisibleSeries((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const valve1Series = extractSeries("valve1Data");
  const valve2Series = extractSeries("valve2Data");

  const filterVisible = (series) =>
    Array.isArray(series) ? series.filter((s) => s.visible !== false) : [];
  const connectionStatus = logStore.connectionStatus;

  const buildRechartsData = (series) => {
    return selectedDevices.map((deviceId) => {
      const name = deviceStore.getDeviceName(deviceId) || deviceId;
      const row = { device: name };

      series.forEach((s) => {
        const deviceIndex = s.data.findIndex(
          (_, idx) => selectedDevices[idx] === deviceId
        );
        row[s.label] = s.data[deviceIndex];
      });

      return row;
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h5"
        sx={{
          mb: 2,
          fontWeight: "bold",
          color: "primary.main",
        }}
      >
        All Device Analytic
      </Typography>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          borderRadius: 2,
          backgroundColor: "#FFFFFF",
          boxShadow: 3,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <FormControl
          sx={{ minWidth: 300 }}
          error={selectedDevices.length > MAX_SELECTION}
        >
          <InputLabel>Show up to 3 devices</InputLabel>
          <Select
            multiple
            value={selectedDevices}
            input={<OutlinedInput label="Show up to 3 devices" />}
            onChange={(e) => {
              const val = e.target.value;
              if (val.length <= MAX_SELECTION) setSelectedDevices(val);
            }}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((id) => (
                  <Chip
                    key={id}
                    label={deviceStore.getDeviceName(id) || id}
                    size="small"
                  />
                ))}
              </Box>
            )}
          >
            {allDeviceIds.map((id) => (
              <MenuItem key={id} value={id}>
                {deviceStore.getDeviceName(id) || id}
              </MenuItem>
            ))}
          </Select>
          {selectedDevices.length > MAX_SELECTION && (
            <FormHelperText>Maximum 3 devices allowed</FormHelperText>
          )}
          {devicesWithoutData.length > 0 && (
            <FormHelperText sx={{ color: "warning.main", mt: 1 }}>
              {devicesWithoutData
                .map((id) => deviceStore.getDeviceName(id) || id)
                .join(", ")}{" "}
              has no available data.
            </FormHelperText>
          )}
        </FormControl>

        <Button
          variant="contained"
          onClick={() =>
            setSelectedDevices(allDeviceIds.slice(0, MAX_SELECTION))
          }
        >
          Refresh
        </Button>
      </Box>

      <ChartContainer
        title="Valve 1 Analytics"
        legendSeries={extractSeries("valve1Data")}
        onToggle={toggleSeriesVisibility}
        sx={{
          backgroundColor: "#FFFFFF",
          boxShadow: 1,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={buildRechartsData(filterVisible(extractSeries("valve1Data")))}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="device" />
            <YAxis />
            <Tooltip />
            {filterVisible(extractSeries("valve1Data")).map((s) => (
              <Bar key={s.label} dataKey={s.label} fill={s.color} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </ChartContainer>

      <ChartContainer
        title="Valve 2 Analytics"
        legendSeries={extractSeries("valve2Data")}
        onToggle={toggleSeriesVisibility}
        sx={{
          backgroundColor: "#FFFFFF",
          boxShadow: 1,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={buildRechartsData(filterVisible(extractSeries("valve2Data")))}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="device" />
            <YAxis />
            <Tooltip />
            {filterVisible(extractSeries("valve2Data")).map((s) => (
              <Bar key={s.label} dataKey={s.label} fill={s.color} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </ChartContainer>
    </Box>
  );
});

export default AnalyticTab;

ChartContainer.propTypes = {
  title: PropTypes.string.isRequired,
  legendSeries: PropTypes.array.isRequired,
  onToggle: PropTypes.func,
  children: PropTypes.node,
  sx: PropTypes.object,
};

CustomLegend.propTypes = {
  series: PropTypes.array.isRequired,
  onToggle: PropTypes.func,
};
